// LoggingContext provider for the AlphaMind IDO Frontend logging system
import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { LoggingService, createLoggingConfig, initializeLoggingService } from '../services/logging.service';
import { LoggingConfig } from '../types/logging.types';

// Context interface
interface LoggingContextType {
  loggingService: LoggingService;
  isEnabled: boolean;
}

// Create context
export const LoggingContext = createContext<LoggingContextType | null>(null);

// Provider props interface
interface LoggingProviderProps {
  children: ReactNode;
  config?: Partial<LoggingConfig>;
}

// LoggingProvider component
export const LoggingProvider: React.FC<LoggingProviderProps> = ({ 
  children, 
  config 
}) => {
  // Initialize logging service
  const loggingService = initializeLoggingService(config);
  const isEnabled = loggingService.isLoggingEnabled();

  // Setup global error handlers
  useEffect(() => {
    if (!isEnabled) return;

    // Handle unhandled JavaScript errors
    const handleError = (event: ErrorEvent) => {
      loggingService.logJavaScriptError(
        new Error(event.message),
        'window',
        'unhandled_error',
        {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          source: 'window.onerror'
        }
      );
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason));

      loggingService.logJavaScriptError(
        error,
        'window',
        'unhandled_promise_rejection',
        {
          source: 'window.onunhandledrejection',
          reason: event.reason
        }
      );
    };

    // Add event listeners
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Log provider initialization
    loggingService.logUserAction(
      'logging_provider_initialized' as any,
      'LoggingProvider',
      {
        sessionId: loggingService.getSessionId(),
        config: {
          enabled: isEnabled,
          endpoint: config?.endpoint || 'default'
        }
      }
    );

    // Cleanup function
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      
      // Log provider cleanup
      loggingService.logUserAction(
        'logging_provider_cleanup' as any,
        'LoggingProvider',
        {
          sessionId: loggingService.getSessionId()
        }
      );
      
      // Cleanup logging service
      loggingService.destroy();
    };
  }, [loggingService, isEnabled, config]);

  // Context value
  const contextValue: LoggingContextType = {
    loggingService,
    isEnabled
  };

  return (
    <LoggingContext.Provider value={contextValue}>
      {children}
    </LoggingContext.Provider>
  );
};

// Hook to use logging context
export const useLoggingContext = (): LoggingContextType => {
  const context = useContext(LoggingContext);
  if (!context) {
    throw new Error('useLoggingContext must be used within a LoggingProvider');
  }
  return context;
};

// Default export
export default LoggingProvider;
